import React from "react";
import Reveal from "./Reveal";
import { Sparkles, Car, Crown, Gem, Clock } from "lucide-react";

const scrollToForm = (voucherType) => {
  const el = document.getElementById("rezervace");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("carspa:select-voucher", { detail: voucherType }));
    }, 400);
  }
};

const VOUCHERS = [
  {
    id: "essential",
    icon: Car,
    name: "Essential",
    tagline: "Péče pro každý den",
    duration: "4–5 hodin",
    price: 2999,
    value: 4499,
    services: [
      "Ruční mytí exteriéru — karoserie, disky, podběhy, prahy a okenní rámečky",
      "Důkladné vysátí celého interiéru — sedadla, koberce, kufr i mezery mezi sedačkami",
      "Tepování látkových sedadel a koberečků hloubkovým extraktorem",
      "Profesionální čištění a péče o kožené sedačky (čisticí + výživný balzám)",
      "Šetrné čištění a renovace plastů — palubní deska, středový tunel, dveřní výplně",
      "Leštění skel, palubního displeje a vnitřních zrcátek",
    ],
  },
  {
    id: "prestige",
    icon: Sparkles,
    name: "Prestige",
    tagline: "Přeměna laku se 2letou zárukou",
    duration: "8–10 hodin",
    price: 6999,
    value: 10499,
    featured: true,
    services: [
      "Kompletní mytí exteriéru včetně disků a podběhů",
      "Kompletní dekontaminace laku — odstranění rzi, asfaltu, letového prachu",
      "Dvoukrokové rozleštění laku — odstranění škrábanců",
      "Aplikace sklokeramické ochrany Koch — písemná záruka 2 roky",
      "Hydrofobní úprava skel a plastů",
    ],
  },
  {
    id: "supreme",
    icon: Crown,
    name: "Supreme",
    tagline: "Vše najednou. Nejvyšší hodnota.",
    duration: "12–14 hodin",
    price: 9999,
    value: 14999,
    services: [
      "Vše z programu Essential (interiér, disky, tepování, sedadla)",
      "Vše z programu Prestige (dekontaminace, leštění, Koch sklokeramika 2 roky)",
      "Prioritní termín v Čestlicích nebo Butovicích",
      "Osobní konzultace o dlouhodobé péči o vůz",
    ],
  },
  {
    id: "allin",
    icon: Gem,
    name: "All In",
    tagline: "Maximální transformace. Vše chráněno.",
    duration: "16–18 hodin",
    price: 13990,
    value: 20990,
    services: [
      "Vše z programu Supreme (exteriér, interiér, Koch sklokeramika 2 roky)",
      "Gyeon Q² LeatherShield — keramická ochrana kůže (záruka 1 rok)",
      "Gyeon Q² Trim — keramika plastů a gumových těsnění",
      "Hloubkové ošetření alcantary a textilních prvků",
      "Ozonování interiéru — odstranění pachů a bakterií",
      "Prioritní termín v Čestlicích i Butovicích",
    ],
  },
];

const fmt = (n) => n.toLocaleString("cs-CZ").replace(/,/g, " ");

export const Offer = () => {
  return (
    <section id="nabidka" data-testid="offer-section" className="relative py-20 sm:py-24 md:py-36 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        <Reveal className="max-w-3xl">
          <div className="text-[10px] sm:text-[11px] tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#D4AF37] mb-5 md:mb-6">
            02 — Akce končí 30. června
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] font-light">
            Tyto ceny vidíte
            <br />
            <span className="italic text-gold-gradient">naposled.</span>
          </h2>
          <p className="mt-5 md:mt-6 text-sm sm:text-base text-zinc-400 font-light leading-relaxed max-w-2xl">
            Po 30. červnu se vracíme na plný ceník — <strong className="text-white">stejnou službu zaplatíte
            o 30–50 % víc</strong>. Voucher zafixujete dnešní cenu na celé <strong className="text-white">3 roky</strong>,
            navíc je přenositelný — můžete ho darovat. Žádný kredit ani poukaz na útratu, ale konkrétní péče
            chemií Koch, Gyeon a Swiss Wax techniky s 10+ lety praxe.
          </p>
        </Reveal>

        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-6">
          {VOUCHERS.map((v, i) => {
            const Icon = v.icon;
            return (
              <Reveal
                key={v.id}
                delay={i * 120}
                className={`relative flex flex-col gold-border p-6 sm:p-8 md:p-10 ${
                  v.featured ? "md:-translate-y-4 ring-1 ring-[#D4AF37]/40" : ""
                }`}
              >
                {v.featured && (
                  <div className="absolute -top-3 left-6 sm:left-8 bg-[#D4AF37] text-black text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] uppercase font-semibold px-3 py-1">
                    Nejoblíbenější
                  </div>
                )}
                <div data-testid={`voucher-card-${v.id}`} className="flex flex-col h-full">
                  <Icon className="text-[#D4AF37] mb-5 md:mb-6" size={32} strokeWidth={1} />
                  <div className="text-[10px] tracking-[0.3em] sm:tracking-[0.35em] uppercase text-[#D4AF37] mb-2">
                    Program {v.name}
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl text-white mb-4">{v.tagline}</h3>

                  <div className="flex items-center gap-2 text-[11px] text-zinc-500 mb-5 tracking-wide">
                    <Clock size={12} strokeWidth={1.4} />
                    <span>Doba realizace · {v.duration}</span>
                  </div>

                  {/* Price comparison block — savings visible at a glance */}
                  <div className="mb-5 md:mb-6">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">
                      Běžná cena
                    </div>
                    <div className="font-display text-2xl sm:text-3xl text-zinc-500 line-through decoration-red-500/70 decoration-2 leading-none">
                      {fmt(v.value)} Kč
                    </div>

                    <div className="mt-3 flex items-baseline gap-2 sm:gap-3">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                        Vaše cena
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-display text-5xl sm:text-6xl text-white font-light leading-none">
                        {fmt(v.price)}
                      </span>
                      <span className="text-lg sm:text-xl text-zinc-400">Kč</span>
                    </div>

                    <div
                      data-testid={`voucher-savings-${v.id}`}
                      className="mt-4 inline-flex items-center gap-2 bg-[#D4AF37] text-black px-3 sm:px-4 py-2 font-semibold"
                    >
                      <span className="text-[10px] sm:text-[11px] tracking-[0.2em] uppercase">
                        Ušetříte
                      </span>
                      <span className="font-display text-lg sm:text-xl leading-none">
                        {fmt(v.value - v.price)} Kč
                      </span>
                    </div>
                  </div>

                  <div className="hairline my-2" />

                  <ul className="space-y-3 my-5 md:my-6 flex-1">
                    {v.services.map((b) => (
                      <li key={b} className="flex gap-3 text-sm text-zinc-300 leading-relaxed">
                        <span className="text-[#D4AF37] mt-[2px]">◆</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => scrollToForm(v.id)}
                    data-testid={`voucher-cta-${v.id}`}
                    className={`btn-gold w-full mt-2 px-6 py-4 text-[11px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] uppercase font-semibold transition-colors ${
                      v.featured
                        ? "bg-[#D4AF37] hover:bg-[#F0C846] text-black"
                        : "bg-transparent border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                    }`}
                  >
                    Zajistit {v.name}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="text-center text-xs text-zinc-500 mt-8 md:mt-10 max-w-2xl mx-auto leading-relaxed px-2">
          Platnost voucheru 3 roky · Přenositelný na kohokoli · Uplatníte v pobočkách Čestlice i Butovice ·
          Použitá chemie: Koch · Gyeon · Swiss Wax
        </p>
      </div>
    </section>
  );
};

export default Offer;
